import {
  VIEWPORT_EPSILON,
  normalizeDrawingRotation,
  toDrawingRotationName,
  type DrawingPlane,
  type DrawingRotation,
  type DrawingRotationInput,
  type SelectionCoords,
} from "./modal-open-pdf.lib";

export type AddToCollectionFormFields = {
  name: string;
  plane: string;
};

export type AddToCollectionMode = "default" | "define";
export type AddToCollectionVariant = "default" | "planeOnly";

export type DrawingPlaneFields = {
  sectionXY?: number[];
  sectionYZ?: number[];
  planXY?: number[];
};

export type DrawingUpdatePayload = DrawingPlaneFields & {
  name?: string;
};

export type AddToCollectionPopupProps = {
  onClose: () => void;
  onCancel?: () => void;
  onUpdated?: (payload: { plane?: DrawingPlane }) => void;
  selectionCoords?: SelectionCoords | null;
  initialSelection?: SelectionCoords | null;
  fileId: string;
  pageNumber: number;
  mode?: AddToCollectionMode;
  variant?: AddToCollectionVariant;
  drawingId?: string;
  initialName?: string;
  initialPlane?: DrawingPlane;
  rotation: DrawingRotation;
  projectId?: string;
};

export const ADD_TO_COLLECTION_POPUP_TITLE = "Добавление страницы в коллекцию";
export const PLANE_ONLY_POPUP_TITLE = "Изменение плоскости";

export const DEFAULT_POSITIONING_HINT =
  "Точность позиционирования и допустимое смещение будут задаваться пользователем на этапе размещения чертежа на информационной модели.";

export const PLANE_ONLY_CHANGE_HINT = "Плоскость чертежа будет изменена, потребуется корректировка позиционирования на привязанных к чертежу моделях";

export const SNACK = {
  projectNotFound: "Не удалось определить проект",
  drawingNotFound: "Не удалось определить чертёж",
  selectionNotFound: "Не удалось определить область выделения",
  planeRequired: "Выберите плоскость",
  planeUpdateFailed: "Не удалось изменить плоскость чертежа",
  drawingUpdateFailed: "Не удалось обновить чертёж",
  fileNameExists: "Файл с таким названием уже существует",
  highlightSaveFailed: "Чертёж создан, но не удалось сохранить область выделения",
  pageAddFailed: "Не удалось добавить страницу в коллекцию",
  areaAddFailed: "Не удалось добавить область в коллекцию",
  planeUpdated: "Плоскость чертежа успешно изменена",
  areaUpdated: "Область чертежа успешно изменена",
} as const;

export const getDebugMessage = (error: unknown): string | undefined => {
  const debugMessage = (error as { response?: { data?: { debugMessage?: unknown } } } | null)?.response?.data?.debugMessage;

  return typeof debugMessage === "string" ? debugMessage : undefined;
};

export const isWholePageCollection = (mode: AddToCollectionMode, selectionCoords?: SelectionCoords | null) => {
  return mode !== "define" && !selectionCoords;
};

export const hasSelectionAreaChanged = (
  selectionCoords: SelectionCoords | null | undefined,
  initialSelection: SelectionCoords | null | undefined,
  epsilon = VIEWPORT_EPSILON,
) => {
  if (!selectionCoords) {
    return false;
  }

  if (!initialSelection) {
    return true;
  }

  return (
    Math.abs(selectionCoords.originX - initialSelection.originX) >= epsilon ||
    Math.abs(selectionCoords.originY - initialSelection.originY) >= epsilon ||
    Math.abs(selectionCoords.areaWidth - initialSelection.areaWidth) >= epsilon ||
    Math.abs(selectionCoords.areaHeight - initialSelection.areaHeight) >= epsilon
  );
};

export const hasDrawingRotationChanged = (rotation: DrawingRotationInput, initialRotation: DrawingRotationInput) => {
  return normalizeDrawingRotation(rotation) !== normalizeDrawingRotation(initialRotation);
};

export const hasDrawingPlaneChanged = (plane: string, initialPlane?: DrawingPlane) => {
  return (plane || "") !== (initialPlane ?? "");
};

export const buildDrawingPlaneUpdatePayload = (planeValue: string, pageNumber: number): DrawingPlaneFields => {
  if (planeValue === "Разрез YZ") {
    return { sectionYZ: [pageNumber], sectionXY: [], planXY: [] };
  }

  if (planeValue === "Разрез XZ") {
    return { sectionXY: [pageNumber], sectionYZ: [], planXY: [] };
  }

  if (planeValue === "План XY") {
    return { planXY: [pageNumber], sectionXY: [], sectionYZ: [] };
  }

  return {};
};

export const buildDrawingPlaneCreateFields = (planeValue: string, pageNumber: number): DrawingPlaneFields => {
  if (planeValue === "Разрез YZ") {
    return { sectionYZ: [pageNumber] };
  }

  if (planeValue === "Разрез XZ") {
    return { sectionXY: [pageNumber] };
  }

  if (planeValue === "План XY") {
    return { planXY: [pageNumber] };
  }

  return {};
};

export const buildCreateDrawingPayload = (params: {
  projectId: string;
  name: string;
  fileId: string;
  pageNumber: number;
  plane: string;
  rotation: DrawingRotationInput;
}) => {
  const { projectId, name, fileId, pageNumber, plane, rotation } = params;

  return {
    projectId,
    name,
    pdfId: fileId,
    page: pageNumber,
    rotation: toDrawingRotationName(rotation),
    ...buildDrawingPlaneCreateFields(plane, pageNumber),
  };
};

export const buildDefineDrawingUpdatePayload = (params: {
  name?: string;
  plane: string;
  pageNumber: number;
  includeName: boolean;
}): DrawingUpdatePayload => {
  const payload: DrawingUpdatePayload = {};

  if (params.includeName && params.name?.trim()) {
    payload.name = params.name.trim();
  }

  Object.assign(payload, buildDrawingPlaneUpdatePayload(params.plane, params.pageNumber));

  return payload;
};

export const getPopupTitle = (variant: AddToCollectionVariant) => {
  return variant === "planeOnly" ? PLANE_ONLY_POPUP_TITLE : ADD_TO_COLLECTION_POPUP_TITLE;
};

export const getSubmitButtonLabel = (mode: AddToCollectionMode, variant: AddToCollectionVariant) => {
  if (variant === "planeOnly" || mode === "define") {
    return "Изменить";
  }

  return "Сохранить";
};

export const getCollectionSuccessTitle = (isWholePage: boolean, name: string) => {
  return `${isWholePage ? "Страница" : "Область"} «${name}» успешно добавлена в коллекцию`;
};

export const getDefineChangeWarningParts = (hasAreaChanged: boolean, hasPlaneChanged: boolean) => {
  return [hasAreaChanged && "Область", hasPlaneChanged && (hasAreaChanged ? "плоскость" : "Плоскость")].filter(Boolean) as string[];
};

export const formatDefineChangeWarning = (hasAreaChanged: boolean, hasPlaneChanged: boolean) => {
  const parts = getDefineChangeWarningParts(hasAreaChanged, hasPlaneChanged);
  return `${parts.join(" и ")} чертежа будет изменена, потребуется корректировка позиционирования на привязанных к чертежу моделях`;
};

export const toDrawingPlane = (plane: string): DrawingPlane | undefined => {
  if (plane === "Разрез YZ" || plane === "Разрез XZ" || plane === "План XY") {
    return plane;
  }

  return undefined;
};
